
import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

export type AdminRole = 'owner' | 'worker';

type AuthContextType = {
  user: User | null;
  session: Session | null;
  role: AdminRole | null;
  isOwner: boolean;
  loading: boolean;
  signIn: (email: string, password: string, turnstileToken?: string) => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const GENERIC_AUTH_ERROR = 'Authentication failed.';

function isLocalhost() {
  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
}

function allowDevDirectLogin() {
  return import.meta.env.VITE_ALLOW_DEV_DIRECT_LOGIN === 'true' && isLocalhost();
}

async function authorizeSession(accessToken: string): Promise<AdminRole | null> {
  const response = await fetch('/api/admin-authorize', {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) return null;
  const result = await response.json().catch(() => null);
  return result?.role === 'owner' || result?.role === 'worker' ? result.role : null;
}

async function loadDevelopmentRole(): Promise<AdminRole | null> {
  const callRpc = supabase.rpc as unknown as (
    name: string,
  ) => Promise<{ data: unknown; error: unknown }>;
  const { data, error } = await callRpc('get_current_admin_profile');
  if (error) return null;
  const profile = Array.isArray(data) ? data[0] : data;
  return profile?.admin_role === 'owner' || profile?.admin_role === 'worker'
    ? profile.admin_role
    : null;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [role, setRole] = useState<AdminRole | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    const applySession = async (session: Session | null) => {
      if (!session?.access_token) {
        if (active) {
          setSession(null);
          setUser(null);
          setRole(null);
          setLoading(false);
        }
        return;
      }

      const resolvedRole = allowDevDirectLogin()
        ? await loadDevelopmentRole().catch(() => null)
        : await authorizeSession(session.access_token).catch(() => null);

      if (!resolvedRole) {
        await supabase.auth.signOut();
        if (active) {
          setSession(null);
          setUser(null);
          setRole(null);
          setLoading(false);
        }
        return;
      }

      if (active) {
        setSession(session);
        setUser(session.user ?? null);
        setRole(resolvedRole);
        setLoading(false);
      }
    };

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      applySession(session);
    });

    // Set up auth listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      applySession(session);
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string, turnstileToken?: string) => {
    try {
      setLoading(true);

      if (allowDevDirectLogin()) {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      } else {
        const response = await fetch('/api/admin-login', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email, password, turnstileToken }),
        });

        const result = await response.json().catch(() => null);
        if (!response.ok || !result?.success || !result?.session?.access_token || !result?.session?.refresh_token) {
          throw new Error(GENERIC_AUTH_ERROR);
        }

        const { error } = await supabase.auth.setSession({
          access_token: result.session.access_token,
          refresh_token: result.session.refresh_token,
        });

        if (error) throw error;
      }
      
      toast.success('Signed in successfully');
    } catch {
      toast.error(GENERIC_AUTH_ERROR);
      throw new Error(GENERIC_AUTH_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        throw error;
      }
      
      toast.success('Signed out successfully');
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Error signing out');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, role, isOwner: role === 'owner', loading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
}
